import React, { useState, useEffect } from 'react';
import { Grid, CssBaseline, Typography, Select, MenuItem } from '@material-ui/core';
import { OfficialProfileNavbar } from '../../components/officialProfileNavbar/officialProfileNavbar';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRolesAndCertsStyles } from './style';
import { OfficialAvatarAndName } from '../../components/officialAvatarAndName/officialAvatarAndName';
import { PersonModel } from '../../models/personModel';
import { getPersonalDetailsByIdReq } from '../../api/person/personalDetailsApi';
import { getRolesByIdReq, updateRoleReq } from '../../api/person/roleApi';
import { CertificationModel } from '../../models/certificationModel';
import { CertificateFamilies } from '../../models/eums/certification/certificateFamilies';
import { showError } from '../../services/notification';
import {
  updateCertificationReq,
  createCertificationReq,
  deleteCertificationReq,
  getCertificationsByIdReq,
} from '../../api/person/certificationApi';
import { RolesModel } from '../../models/rolesModel';
import { PersonRole } from '../../models/eums/personRole';
import { AlertDialog, AlertDialogResult } from '../../components/alertDialog/alertDialog';
import { PersonCertificationTable } from '../../components/personCertificationTable/personCertificationTable';

export function RolesAndCerts() {
  const style = useRolesAndCertsStyles();
  const { t } = useTranslation();
  const { personId } = useParams();
  const [official, setOfficial] = useState<PersonModel>();
  const [personRoles, setPersonRoles] = useState<RolesModel[]>();
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [newRole, setNewRole] = useState<PersonRole>();

  useEffect(() => {
    async function getOfficialProfile() {
      if (!personId?.trim()) return;
      const persDetailsResponse = await getPersonalDetailsByIdReq(personId.trim());
      const rolesResponse = await getRolesByIdReq(personId.trim());

      if (persDetailsResponse && persDetailsResponse.data) setOfficial(persDetailsResponse.data);
      if (rolesResponse && rolesResponse.data) setPersonRoles(rolesResponse.data);
    }
    getOfficialProfile();
  }, [setOfficial, personId]);

  async function addUpdateCertification(certification: CertificationModel, update?: boolean) {
    if (!personId) return;
    let mutableCertification = { ...certification };
    //Had to write date validation manually as material-table does not provide functions for better validation
    mutableCertification.family = CertificateFamilies.OFFICIAL;

    if (!mutableCertification.type) {
      showError('Certification is required');
      return;
    }

    if (update) updateCertificationReq(mutableCertification);
    else createCertificationReq(mutableCertification, personId);
  }

  async function deleteCertification(certification: CertificationModel) {
    if (!personId || !certification._id) return;

    deleteCertificationReq(certification._id);
  }

  async function getCertifications() {
    let dataSet: CertificationModel[] = [];

    if (personId) {
      const resp = await getCertificationsByIdReq(CertificateFamilies.OFFICIAL, personId);
      if (resp && resp.data) dataSet = resp.data;
    }

    return {
      data: dataSet,
      page: 0,
      totalCount: dataSet.length,
    };
  }

  async function changeRole(newRole: PersonRole) {
    setNewRole(newRole);
    setAlertVisible(true);
  }

  async function onAlertClose(result: AlertDialogResult) {
    setAlertVisible(false);
    if (result === AlertDialogResult.CANCEL) return;
    if (!personId || !personRoles || !newRole || personRoles.length === 0) return;

    const currentRoleObj = personRoles[0];
    let updatedRoleObj = { ...currentRoleObj };
    updatedRoleObj.roleType = newRole;
    await updateRoleReq(updatedRoleObj);
    const rolesResponse = await getRolesByIdReq(personId.trim());
    if (rolesResponse && rolesResponse.data) setPersonRoles(rolesResponse.data);
  }

  return (
    <Grid container>
      <CssBaseline />
      <OfficialProfileNavbar selectedIndex={5} personId={personId} />
      <OfficialAvatarAndName official={official} />
      <Grid container item>
        <Grid item container>
          <Grid item xs={12} className={style.titleBox}>
            <Typography variant='h5' className={style.titleText}>
              {t('roles')}
            </Typography>
          </Grid>
          <Grid item>
            <Select
              value={(personRoles && personRoles[0]?.roleType) || ''}
              onChange={ev => {
                changeRole(ev.target.value as PersonRole);
              }}
              variant='outlined'
              className={style.selectRole}
            >
              {Object.values(PersonRole).map((rv, index) => (
                <MenuItem key={index} value={rv}>
                  {rv}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>

        <Grid item container>
          <Grid item xs={12} className={style.titleBox}>
            <Typography variant='h5' className={style.titleText}>
              {t('certifications')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <PersonCertificationTable
              onRowAdd={addUpdateCertification}
              onRowUpdate={reg => addUpdateCertification(reg, true)}
              onRowDelete={deleteCertification}
              data={getCertifications}
            />
          </Grid>
        </Grid>
      </Grid>
      <AlertDialog
        isOpen={alertVisible}
        okBtnText={t('yes')}
        cancelBtnText={t('no')}
        onClose={onAlertClose}
        titleText={t('change_person_role')}
        contentText={t('change_person_role_text')}
      />
    </Grid>
  );
}
